/* Which text each script draws.

   A Qurʾān font is made for one encoding of the text, and drawing another
   encoding with it shows wrong marks: Quran.com's text_uthmani in the KFGQPC
   Uthmanic Hafs font put a dotted circle in every ءَامَنُوا۟ (3,988 silent
   alifs), squashed the hamza of 2:72 (فَٱدَّٰرَٰٔتُمۡ), stacked the iqlāb mīm on
   the tanwīn (أَلِيمٌۢ) and added a small mīm under tanwīn in 4,700 words. So:

   - Uthmani (KFGQPC Uthmanic Hafs font) → the King Fahd Complex's own QPC
     Ḥafṣ text (text_qpc_hafs), which matches the printed Madinah page.
   - IndoPak (IndoPak Nastaleeq font) → text_indopak_nastaleeq, the text that
     font is made for (Quran.com draws the same pair); the older text_indopak
     left its pause signs floating.
   - Amiri Quran / Noto Naskh (adhkar) → text_uthmani, which those fonts
     draw correctly.
   Copying and sharing use text_uthmani: QPC re-uses some code points (ٗ as
   the staggered tanwīn) that other fonts draw as different marks. */
import type { Word } from './api';

const NUMBER_END = /[\s\u00A0]*[\u0660-\u0669]+\s*$/;
const MARKS = '\u0610-\u061A\u064B-\u065F\u0670\u06D6-\u06ED\u08D3-\u08FF';
const LETTER = /[\u0621-\u063A\u0641-\u064A\u0671-\u06D3\u06FA-\u06FF]/;
/* IndoPak āyah ending: a circle (۟), its pause signs, a rukūʿ sign and the number — all but the pause signs are the ornament. */
const INDOPAK_END = new RegExp(`\\s*\\u06DF([${MARKS}]*)[\\uE000-\\uF8FF]*\\s*$`);

/** The QPC Ḥafṣ text of a word (falls back to text_uthmani if the API didn't send it). The word
    list puts a space before the sajdah sign (خُشُوعٗا ۩); the muṣḥaf writes it on the word. */
export const qpcWord = (w: Pick<Word, 'text_qpc_hafs' | 'text_uthmani'>) => (w.text_qpc_hafs || w.text_uthmani).replace(/\s+(?=\u06E9)/g, '');

/** The QPC Ḥafṣ text of an āyah, without its number. */
export const qpcVerse = (v: { text_qpc_hafs?: string; text_uthmani: string }) =>
  v.text_qpc_hafs ? v.text_qpc_hafs.replace(NUMBER_END, '') : v.text_uthmani;

/** Quran.com's Uthmani text, for the Naskh and Amiri fonts — with each pause sign on its word. Written after
    a space (ٱلْقَيُّومُ ۚ) the sign has no letter: it floats in the gap, or draws as a dotted circle. */
export const uthmaniVerse = (v: { text_uthmani: string }) => v.text_uthmani.replace(/\s+(?=[\u06D6-\u06DC])/g, '');

/** The IndoPak Nastaleeq text of an āyah, without its own number ornament (the app numbers āyāt itself). */
export function indopakVerse(v: { text_indopak_nastaleeq?: string }): string | null {
  const t = v.text_indopak_nastaleeq;
  if (!t) return null;
  return t.replace(INDOPAK_END, (_, marks: string) => (marks ? ` ${marks}` : '')).trim();
}

/** The IndoPak text split into the API's words (a pause sign stays with the word before it),
    or null where IndoPak divides the words differently (e.g. اِلۡ يَاسِيۡنَ, 37:130). */
export function indopakWords(v: { text_indopak_nastaleeq?: string; words: Pick<Word, 'char_type_name'>[] }): string[] | null {
  const t = indopakVerse(v);
  if (!t) return null;
  const out: string[] = [];
  for (const tok of t.split(/\s+/)) {
    if (!LETTER.test(tok) && out.length) out[out.length - 1] += ` ${tok}`;
    else out.push(tok);
  }
  return out.length === v.words.filter((w) => w.char_type_name === 'word').length ? out : null;
}

/** Positions of words written joined to the next one in the muṣḥaf (لَّوۡمَا, 15:7, is two words in
    the API's list) — drawn without a space between them. */
export function joinedWords(v: { text_qpc_hafs?: string; text_uthmani: string; words: Pick<Word, 'char_type_name' | 'position' | 'text_qpc_hafs' | 'text_uthmani'>[] }): Set<number> {
  const text = qpcVerse(v), out = new Set<number>();
  let at = 0;
  for (const w of v.words) {
    if (w.char_type_name !== 'word') continue;
    const t = qpcWord(w), i = text.indexOf(t, at);
    if (i < 0) return new Set(); // the texts disagree — assume spaces
    at = i + t.length;
    if (at < text.length && !/\s/.test(text[at])) out.add(w.position);
  }
  return out;
}

/** QPC basmalah, as the muṣḥaf writes it. */
export const BASMALAH_QPC = 'بِسۡمِ ٱللَّهِ ٱلرَّحۡمَٰنِ ٱلرَّحِيمِ';
export const BASMALAH_INDOPAK = 'بِسْمِ اللّٰهِ الرَّحْمٰنِ الرَّحِیْمِ';
